/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["formRef"] }] */

import { IconButton, makeStyles } from '@material-ui/core';
import Button from 'components/Button';
import {
  Field,
  Form,
  FormValues,
  yupSchemaToValidationFunc
} from 'components/Forms';
import DeleteIcon from '@material-ui/icons/Delete';
import { FormApiWithRestart } from 'components/Forms/Form';
import TimeTableSelect from 'components/Forms/TimetableSelect';
import Modal, { useModalStyles } from 'components/Modal';
import ErrorDisplay from 'components/ErrorDisplay';
import Typography from 'components/Typography';
import { DateTime, Interval } from 'luxon';
import Adjustment, {
  AdjustmentPriority,
  AdjustmentType,
  AdjustmentChangeType,
  Pattern
} from 'models/Adjustment';
import React, { useContext, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useAdjustmentsState } from 'store/selectors';
import {
  adjustmentTypeFilter,
  getAdjustmentForDay,
  getAdjustmentGroup
} from 'utils/adjustments';
import { dateToDayOfWeek, getFirstWeekAsDateArray } from 'utils/dates';
import { EngineerCalendarContext } from 'views/Engineers/EngineerCalendar/EngineerCalendar';
import { adjustmentGroupsSlice } from 'views/Engineers/store';
import {
  getEfficiencyOptions,
  TemporaryAdjustmentConstants
} from 'views/Engineers/utils';
import { FormSpy } from 'react-final-form';
import {
  AdjustmentFormButtonActions,
  TemporaryAdjustmentFormContext,
  TemporaryAdjustmentFormModalProps
} from '../AdjustmentFormModals';
import { AdjustmentFormCommonFieldName } from './adjustmentFormCommonConfig';

import {
  efficiencyFormCommonFields,
  efficiencyFormInitialValues,
  efficiencyFormValidationSchema,
  efficiencyFormWeekdayFields,
  getDayFieldName
} from './efficiencyChangeFormConfig';
import { useAdjustmentFormStyles } from './FormStyles';
import { formSpyRelevantDays, useFormDaysOfWeek } from './useFormDaysOfWeek';

const useStyles = makeStyles(
  {
    previousButton: {
      marginRight: 'auto'
    }
  },
  { name: 'TemporaryEfficiencyChangeModal' }
);

function TemporaryEfficiencyFormModalButtons({
  onPrevious,
  onCancel,
  onSubmit,
  showPrevious
}: AdjustmentFormButtonActions) {
  const classes = useStyles();

  return (
    <>
      {showPrevious ? (
        <Button
          key="efficiencyChangeModalPreviousButton"
          color="secondary"
          size="small"
          onClick={onPrevious}
          data-testid="efficiencyChangeModalPreviousButton"
          className={classes.previousButton}
        >
          Previous
        </Button>
      ) : null}

      <Button
        key="efficiencyChangeModalCancelButton"
        color="secondary"
        size="small"
        onClick={onCancel}
        data-testid="efficiencyChangeModalCancelButton"
      >
        Cancel
      </Button>

      <Button
        key="efficiencyChangeModalSubmitButton"
        color="primary"
        size="small"
        onClick={onSubmit}
        data-testid="efficiencyChangeModalSubmitButton"
      >
        Save
      </Button>
    </>
  );
}

export default function TemporaryEfficiencyAdjustmentModal({
  onPrevious,
  onDelete
}: TemporaryAdjustmentFormModalProps) {
  const [formDates, setValueFormSpy, formDaysOfWeek] = useFormDaysOfWeek();
  const { engineerData } = useContext(EngineerCalendarContext);
  const { isOpen, setIsOpen, adjustment } = useContext(
    TemporaryAdjustmentFormContext
  );
  const dispatch = useDispatch();
  const classes = useAdjustmentFormStyles();
  const modalClasses = useModalStyles();
  const formRef = useRef<FormApiWithRestart>();

  const handleSubmitButtonClick = () => {
    formRef.current?.submit();
  };

  const handleClose = () => {
    setIsOpen?.(false);
  };

  const adjustmentsState = useAdjustmentsState();
  const efficiencyAdjustments = adjustmentsState.items.filter(
    adjustmentTypeFilter(AdjustmentType.Efficiency)
  );

  const adjustmentGroup = adjustment?.groupId
    ? getAdjustmentGroup(efficiencyAdjustments, adjustment.groupId)
    : undefined;

  const efficiencyFormSubmitHandler = (fieldValues: FormValues) => {
    const startDate = DateTime.fromISO(
      fieldValues[AdjustmentFormCommonFieldName.ValidFrom] as string
    );
    const endDate = DateTime.fromISO(
      fieldValues[AdjustmentFormCommonFieldName.ValidTo] as string
    );

    const dates = getFirstWeekAsDateArray(
      Interval.fromDateTimes(startDate, endDate)
    );

    const adjustments = dates.reduce((acc: Adjustment[], date) => {
      const day = dateToDayOfWeek(date);

      const adjustmentForDay = getAdjustmentForDay(
        day,
        adjustmentGroup?.adjustments ?? []
      );

      const fieldValuesFormat = date.weekdayLong;
      const currentValue = fieldValues[getDayFieldName(fieldValuesFormat)];

      const optionalId = adjustmentForDay ? { id: adjustmentForDay.id } : {};
      const optionalGroupId = adjustment ? { groupId: adjustment.groupId } : {};

      if (currentValue !== TemporaryAdjustmentConstants.Empty) {
        const adjustmentObj: Adjustment = {
          ...(optionalId as { id: string }),
          ...optionalGroupId,
          reason: fieldValues[AdjustmentFormCommonFieldName.Reason] as string,
          startDate: startDate.toISODate(),
          endDate: endDate.toISODate(),
          pattern: {
            type: Pattern.Weekly,
            weekdays: [day]
          },
          changes: [
            {
              field: AdjustmentType.Efficiency,
              change: fieldValues[getDayFieldName(date.weekdayLong)]
            }
          ] as AdjustmentChangeType[],
          priority: AdjustmentPriority.Temporary
        };

        return [...acc, adjustmentObj];
      }

      return acc;
    }, [] as Adjustment[]);
    const adjustmentsGroupToSave = { adjustments };

    dispatch(
      adjustment
        ? adjustmentGroupsSlice.actions.edit(
            adjustmentsGroupToSave,
            adjustment.groupId,
            engineerData?.id
          )
        : adjustmentGroupsSlice.actions.add(
            adjustmentsGroupToSave,
            engineerData?.id
          )
    );

    handleClose();
  };

  return (
    <Modal
      title="Efficiency Change"
      testId="efficiencyAdjustmentFormModal"
      open={isOpen || false}
      onClose={handleClose}
      modalActions={
        <TemporaryEfficiencyFormModalButtons
          onCancel={handleClose}
          onSubmit={handleSubmitButtonClick}
          onPrevious={onPrevious}
          showPrevious
        />
      }
      additionalButtons={
        adjustment ? (
          <IconButton
            aria-label="delete"
            onClick={onDelete}
            className={modalClasses.button}
            data-testid="deleteEfficiencyAdjustmentIconButton"
          >
            <DeleteIcon />
          </IconButton>
        ) : null
      }
    >
      <Form
        initialValues={efficiencyFormInitialValues(adjustmentGroup)}
        validationFunc={yupSchemaToValidationFunc(
          efficiencyFormValidationSchema
        )}
        onSubmit={efficiencyFormSubmitHandler}
        goBackToPreviousPage={false}
      >
        {({ form }) => {
          formRef.current = form;
          const formState = form.getState();
          return (
            <>
              {efficiencyFormCommonFields.map(field => (
                <Field key={field.id} field={field} />
              ))}
              <Typography variant="h2" className={classes.typography}>
                New Efficiencies
              </Typography>
              {formState.submitFailed && formState.errors?.atLeastOneFilled ? (
                <div className={classes.allEmptyErrorContainer}>
                  <ErrorDisplay
                    status={{
                      message: formState.errors?.atLeastOneFilled
                    }}
                  />
                </div>
              ) : null}
              <div data-testid="efficiencyWeekdaySelectsWrapper">
                {formDaysOfWeek.map(day => (
                  <div
                    className={classes.weekdaysWrapper}
                    key={`timetableWrapper-${day}`}
                  >
                    <Typography
                      data-testid={`efficiencyAdjustmentSelector${day}`}
                      key={day}
                      component="label"
                      htmlFor={`efficiencyChange${day}`}
                    >
                      {day}
                    </Typography>
                    <div className={classes.weekdaySelectWrapper}>
                      <TimeTableSelect
                        field={{
                          ...efficiencyFormWeekdayFields[day],
                          className: classes.weekdaySelect,
                          options: [
                            {
                              value: TemporaryAdjustmentConstants.Empty,
                              key: '',
                              label: ''
                            },
                            ...getEfficiencyOptions()
                          ]
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <FormSpy
                subscription={{
                  values: true
                }}
              >
                {({ values }) => {
                  return formSpyRelevantDays(
                    values,
                    formDates,
                    setValueFormSpy
                  );
                }}
              </FormSpy>
            </>
          );
        }}
      </Form>
    </Modal>
  );
}
